import React, { useCallback, useLayoutEffect, useState } from 'react'

const DISMISS_KEY = 'codam_pwa_install_dismissed_at'
const DISMISS_MS = 14 * 24 * 60 * 60 * 1000
/** 모바일·소형 태블릿(가로 스크롤·설치 안내 노출 구간) */
const COMPACT_MQ = '(max-width: 1199.98px)'

function isStandaloneDisplay() {
  return (
    window.matchMedia?.('(display-mode: standalone)')?.matches ||
    window.navigator.standalone === true
  )
}

function isIos() {
  if (typeof navigator === 'undefined') return false
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  )
}

function isDismissedRecently() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY)
    if (!raw) return false
    const t = parseInt(raw, 10)
    if (Number.isNaN(t)) return false
    return Date.now() - t < DISMISS_MS
  } catch {
    return false
  }
}

function canUseWebShare() {
  return typeof navigator !== 'undefined' && typeof navigator.share === 'function'
}

const PwaInstallBar = () => {
  const [visible, setVisible] = useState(false)
  const [deferredPrompt, setDeferredPrompt] = useState(null)
  const [isCompact, setIsCompact] = useState(false)
  const [showIosGuide, setShowIosGuide] = useState(false)

  const dismiss = useCallback(() => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()))
    } catch {
      /* ignore */
    }
    setVisible(false)
    setDeferredPrompt(null)
    setShowIosGuide(false)
  }, [])

  const tryIosShare = useCallback(async () => {
    if (!canUseWebShare()) {
      dismiss()
      return
    }
    try {
      await navigator.share({
        title: document.title || 'CODAM',
        url: window.location.href,
      })
      dismiss()
    } catch {
      /* 사용자 취소 등 */
    }
  }, [dismiss])

  useLayoutEffect(() => {
    if (typeof window === 'undefined') return undefined
    if (isStandaloneDisplay() || isDismissedRecently()) return undefined

    const mq = window.matchMedia(COMPACT_MQ)

    const sync = () => {
      const compact = mq.matches
      setIsCompact(compact)
      if (!compact) {
        setVisible(false)
        return
      }
      if (isIos()) {
        setDeferredPrompt(null)
        setShowIosGuide(true)
        setVisible(true)
      }
    }

    sync()
    mq.addEventListener('change', sync)

    const onBeforeInstallPrompt = (e) => {
      e.preventDefault()
      if (!mq.matches || isIos()) return
      setShowIosGuide(false)
      setDeferredPrompt(e)
      setVisible(true)
    }
    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt)

    return () => {
      mq.removeEventListener('change', sync)
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt)
    }
  }, [])

  const onInstallClick = useCallback(async () => {
    if (!deferredPrompt) return
    deferredPrompt.prompt()
    try {
      await deferredPrompt.userChoice
    } catch {
      /* ignore */
    }
    setDeferredPrompt(null)
    setVisible(false)
  }, [deferredPrompt])

  if (!visible || !isCompact) return null

  const iosPrimaryLabel = canUseWebShare() ? '공유로 추가' : '확인'

  return (
    <div className="codam-pwa-install" role="region" aria-label="앱 설치 안내">
      <div className="codam-pwa-install__inner">
        <div className="codam-pwa-install__text">
          {deferredPrompt ? (
            <>
              <strong className="codam-pwa-install__title">CODAM 앱으로 설치</strong>
              <span className="codam-pwa-install__desc">
                홈 화면에 추가하면 더 빠르게 열 수 있어요.
              </span>
            </>
          ) : showIosGuide ? (
            <>
              <strong className="codam-pwa-install__title">홈 화면에 추가</strong>
              <span className="codam-pwa-install__desc">
                Safari에서 <span className="codam-pwa-install__mono">공유</span>(□↑) →{' '}
                <span className="codam-pwa-install__mono">홈 화면에 추가</span>를 선택하세요.
              </span>
            </>
          ) : null}
        </div>
        <div className="codam-pwa-install__actions">
          {deferredPrompt ? (
            <button
              type="button"
              className="codam-pwa-install__btn codam-pwa-install__btn--primary"
              onClick={onInstallClick}
            >
              설치
            </button>
          ) : showIosGuide ? (
            <button
              type="button"
              className="codam-pwa-install__btn codam-pwa-install__btn--primary"
              onClick={tryIosShare}
            >
              {iosPrimaryLabel}
            </button>
          ) : null}
          <button
            type="button"
            className="codam-pwa-install__btn codam-pwa-install__btn--ghost"
            onClick={dismiss}
          >
            닫기
          </button>
        </div>
      </div>
    </div>
  )
}

export default PwaInstallBar
